import styled from "styled-components";
import { MetricCard } from "./MetricCard";
import { useVaults, } from "@store";
import { AssetDollars, BigIntValue } from "@components";
import { TimeWall, } from "@components";
import { Loader, Typography, } from "@bracket/ui";
import { cst, } from "@formatters";
import type { FunctionComponent, } from "react";




/**
 * Renders a grid of metric cards displaying key vault information including TVL, NAV, next epoch time,
 * and outstanding shares. Requires an active administrative vault to be present in the vaults slice.
 * 
 * @returns {JSX.Element} A grid of MetricCard components displaying vault metrics
 * @throws {Error} If no administrative vault is present in the vaults slice
 */
export const MetricCards: FunctionComponent = () => {

	const {
		activeAdminVault: vault,
		ready,
	} = useVaults();

	if(!vault) {
		throw new Error("MetricCards expects an administrative vault to be present in the vaults slice");
	}

	if(!ready) {
		return <Loader />;
	}

	const {
		denomination,
		denomination: {
			decimals,
		},
		nav: {
			nav,
		},
		mgmt: {
			nextEpochStartTime,
			sharesOutstanding,
			strategyTVL,
			vanityNav,
		}
	} = vault;

	return (
		<CardContainer>
			<MetricCard
				key="TVL"
				title="TVL"
				token={denomination}
				value={
					<AssetDollars
						amount={strategyTVL.brkt}
						price={strategyTVL.usd}
						variant="h6"
						cents={false}
						precision={2}
						denominator={denomination}
						hideAssetIfUSDC={true}
						dollarSize="1.2rem"
						dollarLineHeight="1.6rem"
					/>
				}
				tooltipMessage={"Total value in the strategy"}
			/>
			<MetricCard
				key="Nav"
				title="NAV"
				token={denomination}
				value={
					<Typography variant="h6" color="var(--text-heading-title)">{cst((Number(nav) / (10**denomination.decimals)).toString(), denomination.symbol === "USDC" ? 6 : 18)}</Typography>
				}
				tooltipMessage={"Most recent Net Asset Value (NAV)"}
				// precision={denomination.decimals}
			/>
			<MetricCard
				key="NextWithdrawal"
				title="Next Epoch End"
				token={denomination}
				value={
					<TimeWall time={nextEpochStartTime} size="xl" />
				}
				tooltipMessage={"Amount due and the date of the next withdrawal deadline"}
			/>
			<MetricCard
				key="SharesOutstanding"
				title="Shares outstanding"
				token={denomination}
				value={
					<Typography variant="h6" color="var(--text-heading-title)">{cst((Number(sharesOutstanding) / (10**decimals)).toString(), denomination.symbol === "USDC" ? 2 : 5)}</Typography>
				}
				tooltipMessage={"Shares of the vault outstanding"}
				precision={2}
			/>
		</CardContainer>
	);
};

const CardContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 8px;
	@media (min-width: 768px) {
		display: flex;
		gap: 12px;
		> * {
			flex: 1;
			width: 100%;
		}
	}
`;
